import type React from 'react';
import type { ReactElement } from 'react';

import { formatDuration } from 'shared/util/formatting';
import {
    differenceInMinutes,
    formatReadableDateFull,
    formatReadableDayTime,
    formatReadableTime,
    safeParseISO,
} from 'shared/util/datetime-fns';

import type { Flight } from '@/interfaces';
import { AirlineLogo } from './AirlineLogo';

export type HotelResultItemFlightDetailsProps = {
    flights: Flight[];
    outbound?: boolean;
};

export const HotelResultItemFlightDetails: React.FC<HotelResultItemFlightDetailsProps> = ({
    flights,
    outbound = false,
}): ReactElement<HotelResultItemFlightDetailsProps> | null => {
    const renderFlight = (flight: Flight): ReactElement => {
        const parsedDepartureTime = safeParseISO(flight.departureTime);
        const parsedArrivalTime = safeParseISO(flight.arrivalTime);

        return (
            <div className="grid" data-testid="generatedFlightDetails">
                {flight.airlineCode && flight.flightNo && (
                    <div className="grid grid__unit u-size1of1">
                        <div className="u-licorice u-d1 grid__unit u-size1of5">
                            <AirlineLogo className="airline-logo" airlineCode={flight.airlineCode} />
                        </div>
                        <div className="grid grid__unit u-size4of5">
                            <div className="grid__unit u-d1 u-size1of4" data-testid="airlineFlightNo">
                                {flight.airlineCode}
                                {flight.flightNo}
                            </div>
                            <div className="grid__unit u-d1 u-size3of4" data-testid="airlineDisplayName">
                                {flight.airline}
                            </div>
                        </div>
                    </div>
                )}
                <div className="u-size1of1 grid__unit flex flex__row flex__align_center">
                    <div className="grid grid__unit u-size4of5">
                        <div className="grid__unit u-licorice u-strong u-size1of1 u-hide-gt768">
                            kl. {formatReadableTime(parsedDepartureTime)}{' '}
                            {parsedArrivalTime ? `- ${formatReadableTime(parsedArrivalTime)}` : ''}
                        </div>
                        <div className="grid__unit u-licorice u-caption u-strong u-r-size1of4 u-hide-lt768" data-testid="departureDate">
                            {formatReadableDayTime(parsedDepartureTime)}
                        </div>
                        <div className="grid__unit u-r-size3of4" data-testid="flightOrigin">
                            {flight.originAirport}
                        </div>
                        <div className="grid__unit u-licorice u-caption u-strong u-r-size1of4 u-hide-lt768" data-testid="arrivalDate">
                            {formatReadableDayTime(parsedArrivalTime)}
                        </div>
                        <div className="grid__unit u-r-size3of4" data-testid="flightDestination">
                            {flight.destinationAirport}
                        </div>
                    </div>
                    <div className="u-size1of5 u-strong u-text-right" data-testid="flightDuration">
                        {formatDuration(flight.flightDuration)}
                    </div>
                </div>
            </div>
        );
    };

    const renderStopover = (index: number, airport: string, time: number | null): ReactElement => (
        <>
            <hr className="line--horizontal-line u-size4of5 u-float-left" />
            <div className="grid">
                <div className="grid grid__unit u-size4of5" data-testid={`hotelFlight-layover-${index}`}>
                    <span className="u-float-left vertical-stopover-dot">·</span>
                    <div className="grid u-float-right u-r-size3of4 u-truncate">mellomlanding i {airport}</div>
                </div>
                {time && time > 0 && (
                    <div className="grid__unit u-size1of5 u-strong u-text-right" data-testid="stopoverDuration">
                        {formatDuration(time)}
                    </div>
                )}
            </div>
            <hr className="line--horizontal-line u-size4of5 u-float-left" />
        </>
    );

    const parsedFirstDepartureTime = safeParseISO(flights[0].departureTime);

    const renderFlights = (): ReactElement => (
        <div className="u-pb32">
            <div className="u-pb16">
                <span className={`icon-pre ${outbound ? 'icon--flight-departure' : 'icon--flight-arrival'} u-float-left u-pr8`} />
                <div className="u-licorice u-caption u-strong u-pt2" data-testid="flightDirection">
                    {outbound ? 'Utreise' : 'Hjemreise'}: {formatReadableDateFull(parsedFirstDepartureTime)}
                </div>
            </div>
            <div className="line--border-left u-pl16 u-mr32">
                {flights.map((flight, index) => (
                    <div
                        key={`${flight.originAirport}-${flight.destinationAirport}-${flight.departureTime}`}
                        data-testid={`hotelFlightResult-element-${index}`}>
                        {index > 0 &&
                            renderStopover(
                                index,
                                flight.originAirport,
                                differenceInMinutes(safeParseISO(flights[index - 1].arrivalTime), safeParseISO(flight.departureTime))
                            )}
                        {renderFlight(flight)}
                    </div>
                ))}
            </div>
        </div>
    );

    return parsedFirstDepartureTime ? renderFlights() : null;
};
